import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { authApi, type AdminUser } from '../lib/authApi';
import { Icon } from '../ui/icons';

function formatDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Every account on the server, with the power to ban, unban, promote or delete one. */
export function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await authApi.adminListUsers());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleDisabled = async (u: AdminUser) => {
    try {
      const updated = await authApi.adminUpdateUser(u.id, { disabled: !u.disabled });
      setUsers((current) => current.map((x) => (x.id === u.id ? { ...x, ...updated } : x)));
    } catch {
      setError('Не удалось изменить статус — возможно, это последний администратор.');
    }
  };

  const toggleSuperAdmin = async (u: AdminUser) => {
    try {
      const updated = await authApi.adminUpdateUser(u.id, { isSuperAdmin: !u.isSuperAdmin });
      setUsers((current) => current.map((x) => (x.id === u.id ? { ...x, ...updated } : x)));
    } catch {
      setError('Не удалось изменить роль — должен остаться хотя бы один суперадмин.');
    }
  };

  const toggleCreateOrgs = async (u: AdminUser) => {
    try {
      const updated = await authApi.adminUpdateUser(u.id, { canCreateOrgs: !u.canCreateOrgs });
      setUsers((current) => current.map((x) => (x.id === u.id ? { ...x, ...updated } : x)));
    } catch {
      setError('Не удалось изменить право на создание организаций.');
    }
  };

  const remove = async (u: AdminUser) => {
    if (!window.confirm(`Удалить аккаунт «${u.name}» (${u.email})? Это нельзя отменить.`)) return;
    try {
      await authApi.adminDeleteUser(u.id);
      setUsers((current) => current.filter((x) => x.id !== u.id));
    } catch {
      setError('Не удалось удалить — должен остаться хотя бы один суперадмин.');
    }
  };

  if (loading) return <div className="dash-loading">Загрузка…</div>;

  return (
    <div className="admin-table">
      {error && (
        <div className="dash-error" role="alert">
          <Icon name="close" size={14} />
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Скрыть">
            <Icon name="close" size={14} />
          </button>
        </div>
      )}

      {users.map((u) => (
        <div key={u.id} className="admin-row">
          <span className="admin-user-avatar" style={{ background: u.color }}>
            {u.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="admin-user-text">
            <span className="admin-user-name">
              {u.name}
              {u.id === me?.id && ' (вы)'}
              {u.isSuperAdmin && <span className="admin-badge admin-badge-super">суперадмин</span>}
              {u.canCreateOrgs && !u.isSuperAdmin && (
                <span className="admin-badge admin-badge-orgs">может создавать орг.</span>
              )}
              {u.disabled && <span className="admin-badge admin-badge-off">заблокирован</span>}
            </span>
            <span className="admin-user-meta">
              {u.email} · с {formatDate(u.createdAt)}
              {u.organizations.length > 0 && ` · ${u.organizations.map((o) => o.name).join(', ')}`}
            </span>
          </span>
          <span className="admin-row-actions">
            <button type="button" className="menu-item" onClick={() => void toggleSuperAdmin(u)}>
              {u.isSuperAdmin ? 'Снять суперадмина' : 'Сделать суперадмином'}
            </button>
            {!u.isSuperAdmin && (
              <button type="button" className="menu-item" onClick={() => void toggleCreateOrgs(u)}>
                {u.canCreateOrgs ? 'Забрать создание орг.' : 'Разрешить создание орг.'}
              </button>
            )}
            <button type="button" className="menu-item" onClick={() => void toggleDisabled(u)}>
              {u.disabled ? 'Разблокировать' : 'Заблокировать'}
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Удалить пользователя"
              data-tip="Удалить"
              onClick={() => void remove(u)}
            >
              <Icon name="trash" size={14} />
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}
